import { Component, OnInit } from '@angular/core';
import {combineLatest as observableCombineLatest } from 'rxjs';
import { transition, trigger, style, animate, keyframes } from '@angular/animations';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { MerchantsService } from '../../../core/services/merchants.service';
import { PaymentsService } from '../../../core/services/payments.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { ToolsService } from '../../../core/services/tools.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { PaymentModalV4Component } from '../payment-modal-v4/payment-modal-v4.component';
import { PlansService } from '../../../core/services/plans.service';
import { AnalyticsService } from '../../../core/services/analytics.service';
import { IntlService } from '../../../core/services/intl.service';
import { CancelSubscriptionModalComponent } from '../cancel-subscription-modal/cancel-subscription-modal.component';

@Component({
  selector: 'app-plans-modal',
  templateUrl: './plans-modal.component.html',
  styleUrls: ['./plans-modal.component.scss'],
  animations: [
    trigger('flyInOut', [
      transition(
        'void => *',
        animate(
          260,
          keyframes([
            style({ opacity: '0', transform: 'scale(1.01)', offset: 0 }),
            style({ opacity: '1', transform: 'scale(1)', offset: 1 }),
          ])
        )
      ),
      transition(
        '* => void',
        animate(
          360,
          keyframes([
            style({ opacity: '1', transform: 'scale(1)', offset: 0 }),
            style({ opacity: '0', transform: 'scale(0.9)', offset: 1 }),
          ])
        )
      ),
    ]),
  ], // animations
})
export class PlansModalComponent implements OnInit {
  public slideNum = 1;
  public providersAmount = 2;
  public minProvidersAmount = 2;

  public merchant;
  public availablePlans;

  public loading = true;
  public errorMsg;
  public activePlan = null;
  public allCurrencies = null;
  public freePlan = null;

  public selectedProPlanAnnually = null;
  public selectedProPlanMonthly = null;

  public selectedProPlusPlanAnnually = null;
  public selectedProPlusPlanMonthly = null;

  public availableCurrencies = [];
  public initialCurrency;
  public supportEmail;
  public supportWhatsapp;
  public withoutSMSlabel;
  public withSMSlabel;
  public annualPlanLabel;
  public monthlyPlanLabel;

  public annualPlan = false;
  public withSMS = true;
  public annualDiscount = 1-0.2;

  public providersAmountOptions = [];

  public paymentModalRef: BsModalRef;
  public annualPlanSubscribed = false;
  public subscriptionStatusActive = false;

  public showProPriceWithoutVat = false;
  public showProPlusPriceWithoutVat = false;

  public showCancelButton = false;
  maxSelfServiceNumberOfProviders: number;

  constructor(
    private bsModalRef: BsModalRef,
    private merchantsService: MerchantsService,
    private modalService: BsModalService,
    private paymentsService: PaymentsService,
    private toolsService: ToolsService,
    private translate: TranslateService,
    public plansService: PlansService,
    public analytics: AnalyticsService,
    private intlService: IntlService
  ) {}

  ngOnInit() {
    this.supportEmail = this.intlService.getSupportEmail();
    this.supportWhatsapp = this.intlService.getContactWhatsapp();
    this.setup();
    this.analytics.trackProductAnalyticsEvent('open_upgrade_modal', {origin: 'navbar'});
  }

  setup() {

    this.maxSelfServiceNumberOfProviders = environment.maxSelfServiceNumberOfProviders;

    // create providerAmountOptions based on maxSelfServiceNumberOfProviders, from 0 to maxSelfServiceNumberOfProviders
    this.providersAmountOptions = Array.from({length: this.maxSelfServiceNumberOfProviders}, (v, k) => k + 1);

    const country = this.intlService.getCurrentCountry();
    const initialRequests = observableCombineLatest(this.merchantsService.getMerchant(), this.paymentsService.getPlans(country));

    this.withSMSlabel = this.translate.instant('SHARED.plansModal.with-sms');
    this.withoutSMSlabel = this.translate.instant('SHARED.plansModal.without-sms');
    this.annualPlanLabel = this.translate.instant('SHARED.plansModal.paid-annual-plan');
    this.monthlyPlanLabel = this.translate.instant('SHARED.plansModal.paid-monthly-plan');

    initialRequests.subscribe((res) => {
      this.merchant = res[0];
      this.annualPlan = this.merchant.stripePlan ? this.merchant?.stripePlan?.endsWith('annual') : true;
      this.allCurrencies = res[1];
      this.selectPlansBasedOnCurrency();
      this.initAvailableCurrencies();
      this.loading = false;
      this.annualPlanSubscribed = this.merchant?.stripePlan?.endsWith('annual');
      if (this.merchant.plan === 'free') {
        this.slideNum = 2;
      } else {
        this.slideNum = this.merchant.plan?.startsWith('per_provider_pro_plus') ? 3 : 2;
        // take into consideration canceled plans (because there is no planQuantity at this point)
        const quantity = this.merchant.numberOfProviders > this.merchant.planQuantity ? this.merchant.numberOfProviders : this.merchant.planQuantity;
        this.providersAmount = quantity;
      }
      this.subscriptionStatusActive = this.merchant.paymentStatus === 'active';
    });
  }

  selectPlansBasedOnCurrency(currencySymbol = null) {

    if (currencySymbol) {
      this.availablePlans = this.allCurrencies.find((v) => v.symbol === currencySymbol);
    } else {
      for (const cry of this.allCurrencies) {
        const plan = cry.plans.find((v) => v.id === this.merchant.stripePlan);

        if (plan) {
          this.availablePlans = cry;
          this.activePlan = plan;
          break;
        }
      }

      if (this.activePlan && this.activePlan.bukId === 'free') {
        // Select merchant currency
        const plans = this.allCurrencies.find((v) => v.currency === this.merchant.currency);
        if (plans) {
          this.availablePlans = plans;
        }
      }
    }

    if (!this.availablePlans) {
      // Select merchant currency
      const plans = this.allCurrencies.find((v) => v.currency === this.merchant.currency);
      if (plans) {
        this.availablePlans = plans;
      } else {
        this.availablePlans = this.allCurrencies[0];
      }
    }

    this.freePlan = this.availablePlans.plans.find((p) => p.bukId === 'free');
    // Only on free, change to selected currency
    this.freePlan.currency = this.availablePlans.currency;

    if (this.activePlan) {
      // OLD users can't select the one provider plan.
      this.minProvidersAmount = this.merchant.id <= 1405 ? 2 : 1;
      if (this.merchant.plan.endsWith('_without_sms')) {
        this.withSMS = false;
      }
      if (this.merchant.plan === 'free') {
        this.providersAmount = this.minProvidersAmount;
      }
    }

    this.refreshPrices();

    if (this.selectedProPlanMonthly?.priceWithoutVat) {
      this.showProPriceWithoutVat = true;
    }
    if (this.selectedProPlusPlanMonthly?.priceWithoutVat) {
      this.showProPlusPriceWithoutVat = true;
    }
  }

  refreshPrices() {

    const perProviderPlans = this.plansService.getPlansBasedOnSettings('pro', this.withSMS, this.availablePlans);
    this.selectedProPlanAnnually = perProviderPlans.annual;
    this.selectedProPlanMonthly = perProviderPlans.monthly;

    const proPlusPlans = this.plansService.getPlansBasedOnSettings('pro_plus', this.withSMS, this.availablePlans);
    this.selectedProPlusPlanAnnually = proPlusPlans.annual;
    this.selectedProPlusPlanMonthly = proPlusPlans.monthly;
  }

  initAvailableCurrencies() {
    this.initialCurrency = this.availablePlans.symbol;
    this.availableCurrencies = [];
    for (const currency of this.allCurrencies) {
      this.availableCurrencies.push(currency.symbol);
    }
  }

  changeCurrency(currencySymbol) {
    this.selectPlansBasedOnCurrency(currencySymbol);
  }

  isActivePlan() {
    if (this.slideNum === 3) {
      // PRO PLUS
      return (this.activePlan?.id === this.selectedProPlusPlanMonthly?.id && !this.annualPlan)
        || (this.activePlan?.id === this.selectedProPlusPlanAnnually?.id && this.annualPlan);
    }

    return (this.activePlan?.id === this.selectedProPlanMonthly?.id && !this.annualPlan)
    || (this.activePlan?.id === this.selectedProPlanAnnually?.id && this.annualPlan);
  }

  getSelectedPlan() {
    if (this.slideNum === 3) {
      // PRO PLUS
      return this.annualPlan ? this.selectedProPlusPlanAnnually : this.selectedProPlusPlanMonthly;
    }

    return this.annualPlan ? this.selectedProPlanAnnually : this.selectedProPlanMonthly;
  }

  activatePlan(stripeId, bukId) {
    if (bukId === 'free') {
      this.downgradeToFree(stripeId);
    } else {

      const hasActivePlan = !(this.merchant?.plan === 'free' || this.merchant?.plan === null || this.merchant?.plan === 'canceled');

      const initialState = {
        planId: stripeId,
        merchant: this.merchant,
        availablePlans: this.availablePlans.plans,
      };

      this.loading = true;
      initialState['quantity'] = this.providersAmount;

      this.paymentModalRef = this.modalService.show(PaymentModalV4Component, { initialState });

      const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
        modalCallback.unsubscribe();
      });
      this.close();
    }
  }

  changePaymentMethod(stripeId) {
    const initialState = {
      planId: stripeId,
      merchant: this.merchant,
      availablePlans: this.availablePlans.plans,
      changePaymentMethodOnly: true,
    };

    this.loading = true;
    initialState['quantity'] = this.merchant.planQuantity;

    this.paymentModalRef = this.modalService.show(PaymentModalV4Component, { initialState });

    const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
      modalCallback.unsubscribe();
    });

    this.close();
  }

  cancelSubscription() {
    this.modalService.show(CancelSubscriptionModalComponent, { class: 'modal-md' });
    this.close();
  }

  downgradeToFree(planId) {
    this.errorMsg = null;
    const initialState = {
      question: this.translate.instant('SHARED.plansModal.downgradeToFree'),
    };
    this.modalService.show(ConfirmModalComponent, { initialState, class: 'second' });
    const modalCallback = this.modalService.onHide.subscribe((reason: string) => {
      if (reason && typeof reason === 'boolean') {
        this.loading = true;
        const payload = {
          planId,
        };
        this.paymentsService.activatePlan(this.merchant.id, payload).subscribe(
          (res) => {
            this.merchantsService.getMerchant().subscribe();
            this.close();
          },
          (err) => {
            this.errorMsg = err.errMsg;
            this.loading = false;
          }
        );
      }
      modalCallback.unsubscribe();
    });
  }

  close() {
    this.bsModalRef.hide();
  }
}
